import type {
  PaginatedResultDto,
  UserCompanyMemberDto,
  UserCompanyGroupDto,
} from '@monorepo/shared';
import { UsersTable } from './UsersTable';
import { GroupsTable } from './GroupsTable';
import { Pagination } from '../ui/Pagination';

interface AdminUsersResultsProps {
  activeTab: 'users' | 'groups';
  canManage: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  usersData?: PaginatedResultDto<UserCompanyMemberDto>;
  isUsersLoading: boolean;
  usersError: boolean;
  onUserAction: (userId: string, action: 'edit' | 'remove' | 'resend') => void;
  onBulkResendUsers?: (userIds: string[]) => void;
  onBulkRemoveUsers?: (userIds: string[]) => void;
  groupsData?: PaginatedResultDto<UserCompanyGroupDto>;
  isGroupsLoading: boolean;
  groupsError: boolean;
  onGroupAction: (groupId: string, action: 'edit' | 'delete' | 'view') => void;
  onBulkDeleteGroups?: (groupIds: string[]) => void;
}

export const AdminUsersResults = ({
  activeTab,
  canManage,
  currentPage,
  onPageChange,
  usersData,
  isUsersLoading,
  usersError,
  onUserAction,
  onBulkResendUsers,
  onBulkRemoveUsers,
  groupsData,
  isGroupsLoading,
  groupsError,
  onGroupAction,
  onBulkDeleteGroups,
}: AdminUsersResultsProps) => {
  if (activeTab === 'users' && !isUsersLoading && !usersError && usersData) {
    return (
      <>
        <UsersTable
          users={usersData.items}
          onUserAction={onUserAction}
          isCurrentUserAdmin={canManage}
          onBulkResend={canManage ? onBulkResendUsers : undefined}
          onBulkRemove={canManage ? onBulkRemoveUsers : undefined}
        />

        {usersData.totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={usersData.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </>
    );
  }

  if (activeTab === 'groups' && !isGroupsLoading && !groupsError && groupsData) {
    return (
      <>
        <GroupsTable
          groups={groupsData.items}
          onGroupAction={onGroupAction}
          onBulkDelete={onBulkDeleteGroups}
          isCurrentUserAdmin={canManage}
        />

        {groupsData.totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={groupsData.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </>
    );
  }

  return null;
};
